import { useEffect, useRef } from 'react';
import './Histogram.css';

interface Props {
  canvasRef: React.RefObject<HTMLCanvasElement>;
  version: number; // bump to recompute
}

// Draws an RGB luminance histogram sampled from the current editor canvas.
export function Histogram({ canvasRef, version }: Props) {
  const outRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const src = canvasRef.current;
    const out = outRef.current;
    if (!src || !out) return;

    const W = 240;
    const H = 90;
    out.width = W;
    out.height = H;
    const ctx = out.getContext('2d');
    if (!ctx) return;

    // Downsample the source into a temp 2D canvas to read pixels.
    const tmp = document.createElement('canvas');
    tmp.width = W;
    tmp.height = H;
    const tctx = tmp.getContext('2d');
    if (!tctx) return;
    try {
      tctx.drawImage(src, 0, 0, W, H);
    } catch {
      return;
    }
    const data = tctx.getImageData(0, 0, W, H).data;

    const bins = 128;
    const r = new Float32Array(bins);
    const g = new Float32Array(bins);
    const b = new Float32Array(bins);
    for (let i = 0; i < data.length; i += 4) {
      r[Math.min(bins - 1, (data[i] * bins) >> 8)]++;
      g[Math.min(bins - 1, (data[i + 1] * bins) >> 8)]++;
      b[Math.min(bins - 1, (data[i + 2] * bins) >> 8)]++;
    }
    const max = Math.max(
      ...r,
      ...g,
      ...b,
    );

    ctx.clearRect(0, 0, W, H);
    ctx.globalCompositeOperation = 'lighter';
    const draw = (arr: Float32Array, color: string) => {
      ctx.fillStyle = color;
      ctx.beginPath();
      ctx.moveTo(0, H);
      for (let i = 0; i < bins; i++) {
        const x = (i / (bins - 1)) * W;
        const y = H - (arr[i] / max) * H;
        ctx.lineTo(x, y);
      }
      ctx.lineTo(W, H);
      ctx.closePath();
      ctx.fill();
    };
    draw(r, 'rgba(255,80,80,0.55)');
    draw(g, 'rgba(80,220,120,0.55)');
    draw(b, 'rgba(90,140,255,0.55)');
  }, [canvasRef, version]);

  return <canvas ref={outRef} className="histogram" />;
}
